/**
 * Flowchart-only Mermaid repair, shared by the API sanitiser (server, at
 * persistence) and the web renderer (browser, late rescue for diagrams that
 * were saved before the server repair existed).
 *
 * Every rule here repairs a failure that was reproduced against the real
 * Mermaid 11.12 parser (see __tests__/mermaidFlowchartRepair.test.ts, which
 * runs the parser itself — string-level tests alone cannot show a repair
 * actually parses):
 *
 *  1. An edge that names a subgraph by its multi-word title
 *     (`subgraph "BillingEngine Modular Monolith"` … `BillingEngine Modular
 *     Monolith --> CC`, or the quoted form) is a parse error. Edges must use a
 *     subgraph id. The title-only header gets an id (`subgraph ID ["Title"]`)
 *     and the edge endpoints are rewritten to it. Only done when the title is
 *     actually referenced as an endpoint.
 *  2. Unquoted parentheses or braces inside a rectangle label, a round or
 *     diamond label that nests them, an edge label or a `subgraph id [label]`
 *     label are a parse error. The label is quoted.
 *  3. A bare lowercase `end` used as a node id is a parse error (reserved
 *     word). It is renamed.
 *
 * Contract (pinned by tests, attacked in the #3716 review log):
 *  - A diagram that already parses is returned byte-for-byte. The rules only
 *    fire on text that is an edge endpoint or an unquoted label; text inside
 *    quoted strings, `|edge labels|`, `-- inline edge text -->`, `%%` comments
 *    and bracketed labels is never touched.
 *  - Idempotent: repair(repair(x)) === repair(x).
 *  - Never throws; on anything it does not understand it leaves the text
 *    unchanged. Linear-time on long lines (oversized input is returned as is).
 */

const MASK = '\u0001';

/** Inputs beyond these are returned untouched: LLM diagrams are far smaller. */
const MAX_CODE_LENGTH = 200_000;
const MAX_LINE_LENGTH = 4_000;

const RESERVED = new Set([
  'end', 'graph', 'subgraph', 'flowchart', 'style', 'class', 'classdef',
  'click', 'default', 'linkstyle', 'direction',
]);

// Arrow operators: -->, ---, -.->, ==>, <-->, --o, --x, ~~~ and the
// "-- text -->" opener. Matched against a masked line (labels already hidden).
const ARROW_SRC = String.raw`(?:<?-{2,}[>ox-]?|<?={2,}>?|<?-\.+-?>?|~{3,})`;
const ARROW_AT_END = new RegExp(`(?:${ARROW_SRC}|&)$`);
const ARROW_AT_START = new RegExp(`^(?:${ARROW_SRC}|&)`);

/** `A -- text --> B`, `A -. text .-> B`, `A == text ==> B`: the text part. */
const EDGE_TEXT_RE = /(--|==|-\.)( +)(.+?)( +)(?:--+[>ox-]|==+>?|\.-+>?)/g;

/**
 * Replace the contents of bracketed groups, quoted strings, |edge labels| and
 * `%%` comment tails with MASK characters of the same length, so structural
 * regexes cannot match inside a label.
 */
function maskLine(line: string): string {
  const out = line.split('');
  let depth = 0;
  let inQuote = false;
  let inPipe = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuote) {
      out[i] = MASK;
      if (ch === '"') inQuote = false;
      continue;
    }
    if (ch === '"') { inQuote = true; out[i] = MASK; continue; }
    if (depth > 0) {
      out[i] = MASK;
      if (ch === '[' || ch === '(' || ch === '{') depth++;
      else if (ch === ']' || ch === ')' || ch === '}') depth--;
      continue;
    }
    if (inPipe) {
      out[i] = MASK;
      if (ch === '|') inPipe = false;
      continue;
    }
    if (ch === '%' && line[i + 1] === '%') {
      for (let j = i; j < line.length; j++) out[j] = MASK;
      break;
    }
    if (ch === '[' || ch === '(' || ch === '{') { depth = 1; out[i] = MASK; continue; }
    if (ch === '|') { inPipe = true; out[i] = MASK; continue; }
  }
  return out.join('');
}

/**
 * Spans (start, end exclusive) of inline edge text in an already-masked line.
 * Linear: bounded by MAX_LINE_LENGTH at the call sites.
 */
function edgeTextSpans(masked: string): Array<[number, number]> {
  const spans: Array<[number, number]> = [];
  EDGE_TEXT_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = EDGE_TEXT_RE.exec(masked)) !== null) {
    const s = m.index;
    const prev = s === 0 ? '' : masked[s - 1];
    if (prev === '-' || prev === '=' || prev === '.') continue; // part of a longer arrow
    const textStart = s + m[1].length + m[2].length;
    spans.push([textStart, textStart + m[3].length]);
  }
  return spans;
}

function applyMask(text: string, spans: Array<[number, number]>): string {
  if (spans.length === 0) return text;
  const out = text.split('');
  for (const [s, e] of spans) for (let i = s; i < e; i++) out[i] = MASK;
  return out.join('');
}

/** Structural view of a line: labels, quotes, |pipes|, comments, edge text hidden. */
function structuralMask(line: string): { masked: string; spans: Array<[number, number]> } {
  const masked = maskLine(line);
  const spans = edgeTextSpans(masked);
  return { masked: applyMask(masked, spans), spans };
}

const NON_EDGE_LINE =
  /^\s*(?:subgraph\b|end\s*$|style\b|classDef\b|class\b|click\b|linkStyle\b|direction\b|%%|graph\b|flowchart\b)/;

/** True when `left` ends with an arrow or `&`, ignoring spaces and a masked |label|. */
function endsWithArrow(left: string): boolean {
  let j = left.length;
  while (j > 0 && (left[j - 1] === MASK || /\s/.test(left[j - 1]))) j--;
  return ARROW_AT_END.test(left.slice(Math.max(0, j - 12), j));
}

/** True when `right` starts (after spaces) with an arrow or `&`. */
function startsWithArrow(right: string): boolean {
  return ARROW_AT_START.test(right.slice(0, 40).trimStart());
}

interface TitledSubgraph {
  lineIndex: number;
  indent: string;
  title: string;
  trail: string;
}

/** True when `title` used bare as an edge endpoint is a parse error. */
function needsId(title: string): boolean {
  if (title !== title.trim() || title === '') return false;
  return /\s/.test(title) || /^end\b/.test(title) || title.includes('@');
}

const TITLE_ONLY_HEADER = /^(\s*)subgraph\s+"([^"\n]+)"(\s*)$/;
const ID_LABEL_HEADER = /^(\s*)subgraph\s+([^\s"[\]]+)\s*\[(.*)\](\s*)$/;

function slugFor(title: string, taken: Set<string>): string {
  const words = title.split(/[^A-Za-z0-9]+/).filter(Boolean);
  let slug = words.join('');
  if (!slug || /^\d/.test(slug)) slug = `sg${slug}`;
  if (RESERVED.has(slug.toLowerCase())) slug = `${slug}Group`;
  let candidate = slug;
  let n = 2;
  while (taken.has(candidate)) candidate = `${slug}${n++}`;
  taken.add(candidate);
  return candidate;
}

const CHAR_BOUNDARY_OK = /[\s;&\u0001]/;

/**
 * An occurrence of a title at [start, end) of `view` is an edge endpoint when
 * its neighbours are separators (or the arrow itself, for compact `A-->T`) and
 * an arrow or `&` is attached on at least one side.
 */
function isEndpoint(view: string, start: number, end: number): boolean {
  const left = view.slice(0, start);
  const right = view.slice(end);
  const arrowBefore = endsWithArrow(left);
  const arrowAfter = startsWithArrow(right);
  if (!arrowBefore && !arrowAfter) return false;
  const before = start === 0 ? '' : view[start - 1];
  const after = end >= view.length ? '' : view[end];
  if (before !== '' && !CHAR_BOUNDARY_OK.test(before) && !(arrowBefore && /[>\-=.|]/.test(before))) return false;
  if (after !== '' && !CHAR_BOUNDARY_OK.test(after) && !(arrowAfter && /[<\-=.~]/.test(after))) return false;
  return true;
}

interface Span { pos: number; len: number }

function findAll(view: string, needle: string): number[] {
  const hits: number[] = [];
  let from = 0;
  for (;;) {
    const at = view.indexOf(needle, from);
    if (at === -1) break;
    hits.push(at);
    from = at + 1;
  }
  return hits;
}

function replaceSpans(line: string, spans: Span[], value: string): string {
  let out = line;
  for (const sp of [...spans].sort((a, b) => b.pos - a.pos)) {
    out = out.slice(0, sp.pos) + value + out.slice(sp.pos + sp.len);
  }
  return out;
}

// ── Step 2: label quoting (a linear scanner, never a backtracking regex) ────

const NEEDS_QUOTE = /[(){}]/;
const SHAPE_START = /^[([/\\>{]/;

function quoteIfNeeded(label: string): string | null {
  if (!NEEDS_QUOTE.test(label)) return null;
  if (label.includes('"')) return null;
  return `"${label}"`;
}

/** Index just past the quoted string that opens at `i`. */
function skipQuote(l: string, i: number): number {
  const close = l.indexOf('"', i + 1);
  return close === -1 ? l.length : close + 1;
}

/** Index just past the bracket group that opens at `i` (depth + quote aware). */
function skipGroup(l: string, i: number): number {
  let depth = 0;
  for (let j = i; j < l.length; j++) {
    const ch = l[j];
    if (ch === '"') { j = skipQuote(l, j) - 1; continue; }
    if (ch === '[' || ch === '(' || ch === '{') depth++;
    else if (ch === ']' || ch === ')' || ch === '}') {
      depth--;
      if (depth === 0) return j + 1;
    }
  }
  return l.length;
}

const ID_CHAR = /[A-Za-z0-9_-]/;
const CLOSER: Record<string, string> = { '[': ']', '(': ')', '{': '}' };

function quoteLabels(l: string): string {
  let out = '';
  let i = 0;
  const n = l.length;
  while (i < n) {
    const ch = l[i];
    if (ch === '%' && l[i + 1] === '%') { out += l.slice(i); break; }
    if (ch === '"') {
      const j = skipQuote(l, i);
      out += l.slice(i, j);
      i = j;
      continue;
    }
    if (ch === '[' || ch === '(' || ch === '{') {
      const afterId = i > 0 && ID_CHAR.test(l[i - 1]);
      const next = l[i + 1] ?? '';
      if (afterId && !SHAPE_START.test(next)) {
        // Plain label directly after a node id: ID[...], ID(...), ID{...}.
        const j = skipGroup(l, i);
        const closer = CLOSER[ch];
        if (l[j - 1] === closer && j - 1 > i) {
          const inner = l.slice(i + 1, j - 1);
          // A rectangle with a stray unbalanced bracket is still quoted
          // (the old behaviour); for ( and { only balanced nesting is touched.
          const q = quoteIfNeeded(inner);
          if (q) {
            out += `${ch}${q}${closer}`;
            i = j;
            continue;
          }
        } else if (ch === '[') {
          // Unbalanced inside: fall back to the first closing bracket.
          const close = l.indexOf(']', i + 1);
          if (close !== -1) {
            const inner = l.slice(i + 1, close);
            const q = quoteIfNeeded(inner);
            if (q) {
              out += `[${q}]`;
              i = close + 1;
              continue;
            }
          }
        }
        out += l.slice(i, j);
        i = j;
        continue;
      }
      // Shape token, quoted label, or a group not attached to an id: copy it.
      const j = skipGroup(l, i);
      out += l.slice(i, j);
      i = j;
      continue;
    }
    if (ch === '|') {
      const close = l.indexOf('|', i + 1);
      if (close === -1) { out += l.slice(i); break; }
      const inner = l.slice(i + 1, close);
      const q = inner.includes('"') ? null : quoteIfNeeded(inner);
      out += q ? `|${q}|` : l.slice(i, close + 1);
      i = close + 1;
      continue;
    }
    out += ch;
    i++;
  }
  return out;
}

// ── Public API ──────────────────────────────────────────────────────────────

export function repairFlowchart(code: string): string {
  try {
    if (code.length > MAX_CODE_LENGTH) return code;
    return repairWithPrelude(code);
  } catch {
    return code;
  }
}

/** YAML front matter (`---` … `---`) is not diagram text: leave it alone. */
function repairWithPrelude(code: string): string {
  const fm = /^(\s*---[ \t]*\r?\n[\s\S]*?\r?\n---[ \t]*\r?\n)([\s\S]*)$/.exec(code);
  if (fm) return fm[1] + repairFlowchartUnsafe(fm[2]);
  return repairFlowchartUnsafe(code);
}

function repairFlowchartUnsafe(code: string): string {
  const lines = code.split('\n');
  const tooLong = (l: string) => l.length > MAX_LINE_LENGTH;

  // Identifiers already in use, so generated ids never collide.
  const taken = new Set<string>(code.match(/[A-Za-z_][A-Za-z0-9_]*/g) ?? []);

  // 1. Title-only subgraphs whose title cannot stand as an edge endpoint.
  // Measured against Mermaid 11.12: whitespace, a leading `end` keyword and
  // `@` fail; other single tokens ("Back-end", "C++", "v1.2", "Q&A") parse as
  // ids already and are left alone, so valid diagrams stay byte-identical.
  const titled: TitledSubgraph[] = [];
  lines.forEach((l, i) => {
    if (tooLong(l)) return;
    const m = TITLE_ONLY_HEADER.exec(l);
    if (m && needsId(m[2])) {
      titled.push({ lineIndex: i, indent: m[1], title: m[2], trail: m[3] });
    }
  });
  // Longest title first, so "Billing Engine Core" is never half-matched by
  // "Billing Engine".
  titled.sort((a, b) => b.title.length - a.title.length);

  const viewCache = new Map<number, { masked: string; spans: Array<[number, number]> }>();
  const viewOf = (i: number) => {
    let v = viewCache.get(i);
    if (!v) { v = structuralMask(lines[i]); viewCache.set(i, v); }
    return v;
  };

  for (const sg of titled) {
    const quotedNeedle = `"${sg.title}"`;
    const edits: Array<{ line: number; spans: Span[] }> = [];
    for (let i = 0; i < lines.length; i++) {
      const l = lines[i];
      if (tooLong(l) || !l.includes(sg.title)) continue;
      const { masked, spans: etSpans } = viewOf(i);

      const spans: Span[] = [];
      // Unquoted reference, matched on the structural view.
      for (const at of findAll(masked, sg.title)) {
        if (isEndpoint(masked, at, at + sg.title.length)) {
          spans.push({ pos: at, len: sg.title.length });
        }
      }
      // Quoted reference "Title": quotes visible, labels still hidden around it.
      if (l.includes(quotedNeedle)) {
        const qview = applyMask(l, etSpans);
        for (const at of findAll(qview, quotedNeedle)) {
          const end = at + quotedNeedle.length;
          const left = maskLine(qview.slice(0, at));
          const right = maskLine(qview.slice(end));
          const before = at === 0 ? '' : qview[at - 1];
          const after = end >= qview.length ? '' : qview[end];
          const arrowBefore = endsWithArrow(left);
          const arrowAfter = startsWithArrow(right);
          if (!arrowBefore && !arrowAfter) continue;
          if (before !== '' && !CHAR_BOUNDARY_OK.test(before) && !(arrowBefore && /[>\-=.|]/.test(before))) continue;
          if (after !== '' && !CHAR_BOUNDARY_OK.test(after) && !(arrowAfter && /[<\-=.~]/.test(after))) continue;
          spans.push({ pos: at, len: quotedNeedle.length });
        }
      }
      if (spans.length) edits.push({ line: i, spans });
    }
    if (edits.length === 0) continue;

    const id = slugFor(sg.title, taken);
    lines[sg.lineIndex] = `${sg.indent}subgraph ${id} ["${sg.title}"]${sg.trail}`;
    viewCache.delete(sg.lineIndex);
    for (const e of edits) {
      lines[e.line] = replaceSpans(lines[e.line], e.spans, id);
      viewCache.delete(e.line);
    }
  }

  // 2. Labels containing parentheses or braces.
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i];
    if (tooLong(l)) continue;

    const hdr = ID_LABEL_HEADER.exec(l);
    if (hdr) {
      const q = quoteIfNeeded(hdr[3]);
      if (q) lines[i] = `${hdr[1]}subgraph ${hdr[2]} [${q}]${hdr[4]}`;
    } else if (!NON_EDGE_LINE.test(l)) {
      lines[i] = quoteLabels(l);
    }
  }

  // 3. Bare lowercase `end` as a node id. One replacement id for the whole
  // diagram, so every mention of the node stays the same node.
  let endId: string | null = null;
  const WORD = /[A-Za-z0-9_]+/g;
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i];
    if (tooLong(l) || !l.includes('end')) continue;
    const { masked } = structuralMask(l);
    const spans: Span[] = [];
    WORD.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = WORD.exec(masked)) !== null) {
      if (m[0] !== 'end') continue;
      const s = m.index;
      const e = s + 3;
      const left = masked.slice(0, s);
      const right = masked.slice(e);
      const before = s === 0 ? '' : masked[s - 1];
      const after = e >= masked.length ? '' : masked[e];
      const arrowBefore = endsWithArrow(left);
      const arrowAfter = startsWithArrow(right);
      // Endpoint of an edge.
      const endpoint = (arrowBefore || arrowAfter)
        && (before === '' || CHAR_BOUNDARY_OK.test(before) || (arrowBefore && /[>\-=.|]/.test(before)))
        && (after === '' || CHAR_BOUNDARY_OK.test(after) || (arrowAfter && /[<\-=.~]/.test(after)));
      // Definition `end[Finish]` at the start of a statement.
      const definition = after === MASK && (left.trim() === '' || /;\s*$/.test(left));
      if (endpoint || definition) spans.push({ pos: s, len: 3 });
    }
    if (spans.length) {
      if (endId === null) endId = slugFor('endNode', taken);
      lines[i] = replaceSpans(l, spans, endId);
    }
  }

  // 3b. The renamed node can also be named by style / click / class lines.
  if (endId !== null) {
    const id = endId;
    for (let i = 0; i < lines.length; i++) {
      lines[i] = lines[i]
        .replace(/^(\s*(?:style|click)\s+)end(?=\s|$)/, `$1${id}`)
        .replace(/^(\s*class\s+)([^\s]+)(\s+\S.*)$/, (whole, pre: string, ids: string, rest: string) =>
          `${pre}${ids.split(',').map(x => (x === 'end' ? id : x)).join(',')}${rest}`,
        );
    }
  }

  return lines.join('\n');
}
